import React, { useState, useMemo, Suspense, lazy } from 'react';
import buildRelationshipGraph from '../../utils/relationshipGraph';
import characterImageUrl from '../../utils/characterImageUrl';

const ForceGraph2D = lazy(() => import('react-force-graph-2d'));

const PROFILE_FIELDS = [
  { key: 'backstory', label: 'Backstory' },
  { key: 'motivations', label: 'Motivations' },
  { key: 'internal_conflict', label: 'Internal Conflict' },
  { key: 'voice_and_mannerisms', label: 'Voice & Mannerisms' },
  { key: 'role_in_story', label: 'Role in Story' },
  { key: 'psychological_profile', label: 'Psychological Profile' },
];

const SCENE_LABELS = {
  intro: 'Intro',
  inciting: 'Inciting',
  midpoint: 'Midpoint',
  climax: 'Climax',
  resolution: 'Resolution',
};

const RELATIONSHIP_TYPE_COLORS = {
  ally: 'text-emerald-400 border-emerald-700/50',
  antagonist: 'text-red-400 border-red-700/50',
  romantic: 'text-pink-400 border-pink-700/50',
  family: 'text-amber-400 border-amber-700/50',
  mentor: 'text-blue-400 border-blue-700/50',
  rival: 'text-orange-400 border-orange-700/50',
  other: 'text-gray-400 border-gray-600/50',
};

function ProgressStep({ label, done, active }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${done ? 'text-green-400' : active ? 'text-yellow-400' : 'text-gray-500'}`}>
      <span className={`w-2 h-2 rounded-full ${done ? 'bg-green-500' : active ? 'bg-yellow-500 animate-pulse' : 'bg-gray-600'}`} />
      {label}
    </div>
  );
}

function RelationshipGraph({ centerName, relationships }) {
  const graphData = useMemo(
    () => buildRelationshipGraph(centerName, relationships),
    [centerName, relationships]
  );

  if (!graphData.links.length) {
    return (
      <p className="text-sm text-gray-500 italic py-8 text-center">
        No relationships to visualize yet.
      </p>
    );
  }

  return (
    <div className="h-[360px] w-full rounded-xl bg-gray-950 border border-gray-700 overflow-hidden">
      <Suspense fallback={<div className="h-full flex items-center justify-center text-gray-500 text-sm">Loading graph...</div>}>
        <ForceGraph2D
          graphData={graphData}
          nodeLabel="name"
          nodeCanvasObject={(node, ctx, globalScale) => {
            const label = node.name;
            const fontSize = 12 / globalScale;
            const isCenter = node.isCenter;
            ctx.beginPath();
            ctx.arc(node.x, node.y, isCenter ? 8 : 5, 0, 2 * Math.PI, false);
            ctx.fillStyle = isCenter ? '#fbbf24' : '#60a5fa';
            ctx.fill();
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#e5e7eb';
            ctx.fillText(label, node.x, node.y + 12);
          }}
          linkCanvasObject={(link, ctx) => {
            const start = link.source;
            const end = link.target;
            if (!start?.x || !end?.x) return;
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
            ctx.lineWidth = 1;
            ctx.stroke();
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;
            ctx.font = '9px Sans-Serif';
            ctx.fillStyle = '#9ca3af';
            ctx.textAlign = 'center';
            ctx.fillText(link.type || '', midX, midY - 4);
          }}
          linkDirectionalArrowLength={4}
          linkDirectionalArrowRelPos={1}
          cooldownTicks={80}
          onEngineStop={() => {}}
        />
      </Suspense>
    </div>
  );
}

export default function CharacterDeepDivePanel({ data, onClose }) {
  const [relView, setRelView] = useState('list');

  const relationships = data?.relationships || [];
  const groupedRels = useMemo(() => {
    const groups = {};
    relationships.forEach((rel) => {
      const type = (rel.type || 'other').toLowerCase();
      if (!groups[type]) groups[type] = [];
      groups[type].push(rel);
    });
    return groups;
  }, [relationships]);

  if (!data) return null;

  const {
    character_name: characterName,
    status,
    profile = {},
    scene_images: sceneImages = [],
    themes = [],
    progress = {},
    portrait_url: portraitUrl,
    character,
    error,
    cost_breakdown: costBreakdown,
  } = data;

  const portrait = portraitUrl || characterImageUrl(character);
  const isRunning = status === 'RUNNING' || status === 'PENDING';
  const isFailed = status === 'FAILED';

  return (
    <div className="flex flex-col max-h-[90vh]">
      <div className="flex items-start justify-between p-5 border-b border-gray-700 bg-gray-900/80">
        <div className="flex gap-4 items-start">
          {portrait && (
            <img
              src={portrait}
              alt={characterName}
              className="w-20 h-20 rounded-lg object-cover border border-gray-600 shadow-lg"
            />
          )}
          <div>
            <h2 className="text-2xl font-bold text-white">{characterName}</h2>
            <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Character Deep Dive</p>
            {isRunning && (
              <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-700/50">
                {status === 'PENDING' ? 'Queued…' : 'Generating…'}
              </span>
            )}
            {status === 'COMPLETED' && (
              <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-green-900/40 text-green-300 border border-green-700/50">
                Complete
              </span>
            )}
            {isFailed && (
              <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-red-900/40 text-red-300 border border-red-700/50">
                Failed
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-white text-3xl leading-none font-bold"
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="overflow-y-auto flex-1 p-5 space-y-8 bg-gray-950">
        {isRunning && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-4 rounded-xl bg-gray-900/60 border border-gray-700">
            <ProgressStep label="Profile" done={progress.profile_complete} active={!progress.profile_complete} />
            <ProgressStep label="Scenes" done={progress.scenes_complete} active={progress.profile_complete && !progress.scenes_complete} />
            <ProgressStep label="Relationships" done={progress.relationships_complete} active={progress.scenes_complete && !progress.relationships_complete} />
            <ProgressStep label="Themes" done={progress.themes_complete} active={progress.relationships_complete && !progress.themes_complete} />
            <ProgressStep label="Images" done={progress.images_complete} active={progress.themes_complete && !progress.images_complete} />
          </div>
        )}

        {isFailed && error && (
          <div className="p-4 rounded-xl bg-red-900/20 border border-red-800 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Profile */}
        {(Object.keys(profile).length > 0 || progress.profile_complete) && (
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-4">Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROFILE_FIELDS.map(({ key, label }) =>
                profile[key] ? (
                  <div key={key} className="p-4 rounded-xl bg-gray-900/60 border border-gray-700/50">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-2">
                      {label}
                    </span>
                    <p className="text-sm text-gray-300 leading-relaxed">{profile[key]}</p>
                  </div>
                ) : null
              )}
            </div>
          </section>
        )}

        {/* Scene arc */}
        {(sceneImages.length > 0 || progress.scenes_complete) && (
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">
              Arc Scenes ({sceneImages.length || 5})
            </h3>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {sceneImages.map((scene) => (
                <div
                  key={scene.label}
                  className="flex-shrink-0 w-44 sm:w-52 flex flex-col gap-2"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    {SCENE_LABELS[scene.label] || scene.label}
                  </span>
                  {scene.image_url ? (
                    <img
                      src={scene.image_url}
                      alt={scene.title || scene.label}
                      className="w-full aspect-[3/4] object-cover rounded-lg border border-gray-700"
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] rounded-lg border border-dashed border-gray-700 bg-gray-900/40 flex items-center justify-center text-xs text-gray-600">
                      {isRunning && !progress.images_complete ? 'Generating…' : 'No image'}
                    </div>
                  )}
                  {scene.title && (
                    <p className="text-xs font-semibold text-gray-200">{scene.title}</p>
                  )}
                  {scene.description && (
                    <p className="text-[11px] text-gray-400 leading-snug line-clamp-4">{scene.description}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Relationships */}
        {(relationships.length > 0 || progress.relationships_complete) && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-violet-400">Relationships</h3>
              <div className="flex rounded-lg border border-gray-700 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setRelView('list')}
                  className={`px-3 py-1.5 ${relView === 'list' ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                >
                  List
                </button>
                <button
                  type="button"
                  onClick={() => setRelView('graph')}
                  className={`px-3 py-1.5 ${relView === 'graph' ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                >
                  Graph
                </button>
              </div>
            </div>
            {relView === 'graph' ? (
              <RelationshipGraph centerName={characterName} relationships={relationships} />
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedRels).map(([type, rels]) => (
                  <div key={type}>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${RELATIONSHIP_TYPE_COLORS[type] || RELATIONSHIP_TYPE_COLORS.other}`}>
                      {type}
                    </span>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {rels.map((rel) => (
                        <div
                          key={rel.name}
                          className="p-4 rounded-xl bg-gray-900/60 border border-gray-700/50"
                        >
                          <h4 className="font-semibold text-gray-100">{rel.name}</h4>
                          <p className="text-sm text-gray-400 mt-2 leading-relaxed">{rel.dynamic}</p>
                          {rel.scenes?.length > 0 && (
                            <ul className="mt-2 text-xs text-gray-500 list-disc list-inside">
                              {rel.scenes.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Themes */}
        {(themes.length > 0 || progress.themes_complete) && (
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-400 mb-4">Themes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {themes.map((t, i) => (
                <div key={i} className="p-4 rounded-xl bg-gray-900/60 border border-gray-700/50">
                  <h4 className="font-semibold text-emerald-300">{t.theme}</h4>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{t.evidence}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {costBreakdown?.total != null && status === 'COMPLETED' && (
          <p className="text-xs text-gray-500 font-mono text-right">
            Estimated cost: ${Number(costBreakdown.total).toFixed(4)}
          </p>
        )}
      </div>
    </div>
  );
}
